// Theme Manager Hook for Landing Page

import { useState, useMemo, useEffect } from "react";

import { PaletteMode } from "@mui/material";

/**
 * Get system theme preference
 */
const getSystemTheme = (): PaletteMode => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * Theme management hook for landing page.
 * Always follows the system theme preference — no manual toggle or
 * localStorage persistence.
 */
export const useThemeManager = () => {
  const [mode, setMode] = useState<PaletteMode>(getSystemTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? "dark" : "light");
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const themeInfo = useMemo(
    () => ({
      mode,
      isDark: mode === "dark",
      isLight: mode === "light",
    }),
    [mode],
  );

  return themeInfo;
};
